import { Controller } from "@hotwired/stimulus"

const prefersReducedMotionQuery = window.matchMedia
  ? window.matchMedia("(prefers-reduced-motion: reduce)")
  : { matches: false }

// Page transition animations
// Usage: data-controller="transition" data-transition-type-value="slide-up"
export default class extends Controller {
  static values = {
    type: { type: String, default: "page-pop" }, // page-pop, fade-in, slide-up, etc.
    delay: { type: Number, default: 0 }
  }

  initialize() {
    this.initialOpacity = this.element.style.opacity || ""
    this.animationClass = null
    this.timeoutId = null
    this.rafId = null
  }

  connect() {
    // Global kill-switch to avoid flicker on Turbo navigations
    if (window.disableComponentTransitions !== false) {
      this.showImmediately()
      return
    }

    this.animationClass = `animate-${this.typeValue}`

    if (this.shouldSkipAnimation()) {
      this.showImmediately()
      return
    }

    this.scheduleTransition()
  }

  disconnect() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  scheduleTransition() {
    if (this.delayValue > 0) {
      this.timeoutId = setTimeout(() => {
        this.timeoutId = null
        this.applyTransition()
      }, this.delayValue)
    } else {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null
        this.applyTransition()
      })
    }
  }

  applyTransition() {
    if (!this.animationClass) return

    this.element.classList.remove(this.animationClass)
    // Trigger reflow to restart animation if Turbo reconnects quickly
    void this.element.offsetWidth
    this.element.classList.add(this.animationClass)
  }

  beforeCache() {
    if (this.animationClass) {
      this.element.classList.remove(this.animationClass)
    }
  }

  shouldSkipAnimation() {
    if (this.prefersReducedMotion()) return true
    if (window.isInitialPageLoad) return true
    if (document.documentElement.hasAttribute('data-turbo-preview')) return true
    if (document.body.classList.contains('page-entering')) return true
    if (window.activeViewTransition) return true
    if (window.disableComponentTransitions !== false) return true

    return false
  }

  showImmediately() {
    if (this.animationClass) {
      this.element.classList.remove(this.animationClass)
    }
    this.element.style.opacity = this.initialOpacity || "1"
  }

  prefersReducedMotion() {
    if (typeof window.prefersReducedMotion === "boolean") {
      return window.prefersReducedMotion
    }

    return prefersReducedMotionQuery.matches
  }
}
